import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  CalendarMonth,
  Add,
  Edit,
  Delete,
  Schedule as ScheduleIcon,
  Twitter,
  Facebook,
  Instagram,
  VideoLibrary,
  Event,
  ViewWeek,
  ViewDay,
  Refresh,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { socialMediaApi } from '../../api/socialMediaApi';

interface ScheduledPost {
  scheduleId: string;
  postType: string;
  platforms: string[];
  scheduledTime: string;
  frequency: 'daily' | 'weekly' | 'once';
  enabled: boolean;
  lastRun?: string;
  nextRun: string;
  config: {
    contentType: string;
    autoApprove: boolean;
    customText?: string;
  };
}

interface ScheduleManagerProps {
  onAlert: (type: 'success' | 'error' | 'info', message: string) => void;
}

const ScheduleManager: React.FC<ScheduleManagerProps> = ({ onAlert }) => {
  const [schedules, setSchedules] = useState<ScheduledPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<ScheduledPost | null>(null);
  const [formData, setFormData] = useState({
    postType: 'daily-reveal',
    platforms: [] as string[],
    scheduledTime: dayjs(),
    frequency: 'daily' as 'daily' | 'weekly' | 'once',
    enabled: true,
    contentType: 'carousel',
    autoApprove: false,
  });

  useEffect(() => {
    fetchSchedules();
  }, []);

  const fetchSchedules = async () => {
    try {
      setLoading(true);
      const response = await socialMediaApi.getSchedules();
      setSchedules(response.schedules);
    } catch (error) {
      onAlert('error', 'Failed to fetch schedules');
      console.error('Error fetching schedules:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSchedule = async () => {
    try {
      await socialMediaApi.createSchedule({
        ...formData,
        scheduledTime: formData.scheduledTime.toISOString(),
      });
      onAlert('success', 'Schedule created successfully');
      setEditDialogOpen(false);
      resetForm();
      fetchSchedules();
    } catch (error) {
      onAlert('error', 'Failed to create schedule');
      console.error('Error creating schedule:', error);
    }
  };

  const handleUpdateSchedule = async () => {
    if (!selectedSchedule) return;

    try {
      await socialMediaApi.updateSchedule(selectedSchedule.scheduleId, {
        ...formData,
        scheduledTime: formData.scheduledTime.toISOString(),
      });
      onAlert('success', 'Schedule updated successfully');
      setEditDialogOpen(false);
      setSelectedSchedule(null);
      resetForm();
      fetchSchedules();
    } catch (error) {
      onAlert('error', 'Failed to update schedule');
      console.error('Error updating schedule:', error);
    }
  };

  const handleDeleteSchedule = async (scheduleId: string) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) return;

    try {
      await socialMediaApi.deleteSchedule(scheduleId);
      onAlert('info', 'Schedule deleted');
      fetchSchedules();
    } catch (error) {
      onAlert('error', 'Failed to delete schedule');
      console.error('Error deleting schedule:', error);
    }
  };

  const handleToggleSchedule = async (scheduleId: string, enabled: boolean) => {
    try {
      const schedule = schedules.find(s => s.scheduleId === scheduleId);
      if (!schedule) {
        onAlert('error', 'Schedule not found');
        return;
      }

      await socialMediaApi.updateSchedule(scheduleId, {
        postType: schedule.postType,
        platforms: schedule.platforms,
        scheduledTime: schedule.scheduledTime,
        frequency: schedule.frequency,
        enabled: enabled,
        contentType: schedule.config.contentType,
        autoApprove: schedule.config.autoApprove,
      });
      onAlert('success', `Schedule ${enabled ? 'enabled' : 'disabled'}`);
      fetchSchedules();
    } catch (error) {
      onAlert('error', 'Failed to toggle schedule');
      console.error('Error toggling schedule:', error);
    }
  };

  const handleEdit = (schedule: ScheduledPost) => {
    setSelectedSchedule(schedule);
    setFormData({
      postType: schedule.postType,
      platforms: schedule.platforms,
      scheduledTime: dayjs(schedule.scheduledTime),
      frequency: schedule.frequency,
      enabled: schedule.enabled,
      contentType: schedule.config.contentType,
      autoApprove: schedule.config.autoApprove,
    });
    setEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      postType: 'daily-reveal',
      platforms: [],
      scheduledTime: dayjs(),
      frequency: 'daily',
      enabled: true,
      contentType: 'carousel',
      autoApprove: false,
    });
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'twitter':
        return <Twitter sx={{ fontSize: 16 }} />;
      case 'facebook':
        return <Facebook sx={{ fontSize: 16 }} />;
      case 'instagram':
        return <Instagram sx={{ fontSize: 16 }} />;
      case 'tiktok':
        return <VideoLibrary sx={{ fontSize: 16 }} />;
      default:
        return null;
    }
  };

  const getFrequencyColor = (frequency: string) => {
    switch (frequency) {
      case 'daily':
        return 'primary';
      case 'weekly':
        return 'secondary';
      case 'once':
        return 'default';
      default:
        return 'default';
    }
  };

  const renderListView = () => (
    <List>
      {schedules.map((schedule) => (
        <ListItem key={schedule.scheduleId} sx={{ mb: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
          <ListItemIcon>
            <ScheduleIcon color={schedule.enabled ? 'primary' : 'disabled'} />
          </ListItemIcon>
          <ListItemText
            primary={
              <Box display="flex" alignItems="center">
                <Typography variant="subtitle1">
                  {schedule.postType}
                </Typography>
                <Chip
                  label={schedule.frequency}
                  color={getFrequencyColor(schedule.frequency) as any}
                  size="small"
                  sx={{ ml: 1 }}
                />
                {!schedule.enabled && (
                  <Chip label="Disabled" size="small" sx={{ ml: 1 }} />
                )}
              </Box>
            }
            secondary={
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Next run: {new Date(schedule.nextRun).toLocaleString()}
                </Typography>
                <Box display="flex" gap={0.5} mt={0.5}>
                  {schedule.platforms.map((platform) => (
                    <Chip
                      key={platform}
                      icon={getPlatformIcon(platform) || undefined}
                      label={platform}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            }
          />
          <ListItemSecondaryAction>
            <IconButton
              edge="end"
              aria-label="edit"
              onClick={() => handleEdit(schedule)}
              sx={{ mr: 1 }}
            >
              <Edit />
            </IconButton>
            <IconButton
              edge="end"
              aria-label="delete"
              onClick={() => handleDeleteSchedule(schedule.scheduleId)}
            >
              <Delete />
            </IconButton>
          </ListItemSecondaryAction>
        </ListItem>
      ))}
    </List>
  );

  const renderCalendarView = () => {
    const currentWeek = dayjs().startOf('week');
    const weekDays = Array.from({ length: 7 }, (_, i) => currentWeek.add(i, 'day'));

    return (
      <Grid container spacing={1}>
        {weekDays.map((day) => (
          <Grid item xs={12} md key={day.toISOString()}>
            <Card sx={{ minHeight: 200 }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  {day.format('ddd, MMM D')}
                </Typography>
                {schedules
                  .filter((s) => dayjs(s.nextRun).isSame(day, 'day'))
                  .map((schedule) => (
                    <Box
                      key={schedule.scheduleId}
                      sx={{
                        p: 1,
                        mb: 1,
                        bgcolor: schedule.enabled ? 'primary.light' : 'grey.300',
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="caption" display="block">
                        {dayjs(schedule.nextRun).format('HH:mm')}
                      </Typography>
                      <Typography variant="body2">
                        {schedule.postType}
                      </Typography>
                    </Box>
                  ))}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6">
            Posting Schedule
          </Typography>
          <Box display="flex" gap={2}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, value) => value && setViewMode(value)}
              size="small"
            >
              <ToggleButton value="list">
                <ViewDay />
              </ToggleButton>
              <ToggleButton value="calendar">
                <ViewWeek />
              </ToggleButton>
            </ToggleButtonGroup>
            <Button
              startIcon={<Refresh />}
              onClick={fetchSchedules}
              variant="outlined"
            >
              Refresh
            </Button>
            <Button
              startIcon={<Add />}
              onClick={() => {
                setSelectedSchedule(null);
                resetForm();
                setEditDialogOpen(true);
              }}
              variant="contained"
            >
              Add Schedule
            </Button>
          </Box>
        </Box>

        {schedules.length === 0 ? (
          <Alert severity="info">
            No schedules configured. Click "Add Schedule" to create your first automated posting schedule.
          </Alert>
        ) : viewMode === 'list' ? (
          renderListView()
        ) : (
          renderCalendarView()
        )}

        {/* Edit/Create Dialog */}
        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {selectedSchedule ? 'Edit Schedule' : 'Create Schedule'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Post Type</InputLabel>
                  <Select
                    value={formData.postType}
                    onChange={(e) => setFormData({ ...formData, postType: e.target.value })}
                    label="Post Type"
                  >
                    <MenuItem value="daily-reveal">Daily Reveal</MenuItem>
                    <MenuItem value="carousel">Carousel</MenuItem>
                    <MenuItem value="leaderboard">Leaderboard</MenuItem>
                    <MenuItem value="teaser">Today's Teaser</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Frequency</InputLabel>
                  <Select
                    value={formData.frequency}
                    onChange={(e) => setFormData({ ...formData, frequency: e.target.value as any })}
                    label="Frequency"
                  >
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="once">Once</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <DateTimePicker
                  label="Scheduled Time"
                  value={formData.scheduledTime}
                  onChange={(value) => value && setFormData({ ...formData, scheduledTime: value })}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" gutterBottom>
                  Platforms
                </Typography>
                <Box display="flex" gap={1}>
                  {['twitter', 'facebook', 'instagram', 'tiktok'].map((platform) => (
                    <Chip
                      key={platform}
                      label={platform}
                      icon={getPlatformIcon(platform) || undefined}
                      onClick={() => {
                        const platforms = formData.platforms.includes(platform)
                          ? formData.platforms.filter((p) => p !== platform)
                          : [...formData.platforms, platform];
                        setFormData({ ...formData, platforms });
                      }}
                      color={formData.platforms.includes(platform) ? 'primary' : 'default'}
                      variant={formData.platforms.includes(platform) ? 'filled' : 'outlined'}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={selectedSchedule ? handleUpdateSchedule : handleCreateSchedule}
              variant="contained"
            >
              {selectedSchedule ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default ScheduleManager;